import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GuidelinesInlineSummary } from "@/components/GuidelinesDialog";
import {
  Share2,
  Layers,
  Target,
  FolderPlus,
  Loader2,
  Download,
  CheckSquare,
  Square,
  Users,
  Heart,
  X,
  Crown,
  Grid3X3,
  ListTodo,
  Copy,
  MapPin,
} from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";

/**
 * ShareToCommunityDialog - Dialog for sharing organizers, challenges, and game planners
 */
function ProNotice() {
  let paywallEnabled = false;
  let isPro = true;
  try {
    const sub = useSubscription();
    paywallEnabled = sub.paywallEnabled;
    isPro = sub.isPro;
  } catch {}
  if (!paywallEnabled || isPro) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
      <Crown className="w-4 h-4 text-purple-400 flex-shrink-0" />
      <p className="text-xs text-purple-300">
        Planners use achievement notes & checklists, which require <span className="font-semibold">Pro</span>. You can still browse and adopt, but some features will be locked until you upgrade.
      </p>
    </div>
  );
}

export function ShareToCommunityDialog({
  open,
  onOpenChange,
  game,
  groupedAchievements,
  challenges,
  onShare,
  shareSubmitting,
}) {
  const [shareTab, setShareTab] = useState("organizers");
  const [selectedOrganizersToShare, setSelectedOrganizersToShare] = useState([]);
  const [selectedChallengesToShare, setSelectedChallengesToShare] = useState([]);
  const [selectedOrganizersForPlanner, setSelectedOrganizersForPlanner] = useState([]);
  const [shareDescription, setShareDescription] = useState("");
  const [shareShowSubmitter, setShareShowSubmitter] = useState(true);
  const [newGamePlanner, setNewGamePlanner] = useState({ name: "", description: "" });
  const [shareTags, setShareTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [includeScorecards, setIncludeScorecards] = useState(false);

  const handleClose = (openState) => {
    if (!openState) {
      setSelectedOrganizersToShare([]);
      setSelectedChallengesToShare([]);
      setSelectedOrganizersForPlanner([]);
      setShareDescription("");
      setNewGamePlanner({ name: "", description: "" });
      setShareTags([]);
      setTagInput("");
      setIncludeScorecards(false);
    }
    onOpenChange(openState);
  };

  const handleSubmit = async () => {
    await onShare({
      shareTab,
      selectedOrganizersToShare,
      selectedChallengesToShare,
      selectedOrganizersForPlanner,
      shareDescription,
      shareShowSubmitter,
      newGamePlanner,
      shareTags,
      includeScorecards,
    });
    handleClose(false);
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !shareTags.includes(tag)) {
      setShareTags(prev => [...prev, tag]);
    }
    setTagInput("");
  };

  const handleRemoveTag = (tagToRemove) => {
    setShareTags(prev => prev.filter(t => t !== tagToRemove));
  };

  const sections = groupedAchievements?.baseGame?.sections || [];
  const dlcs = groupedAchievements?.dlcs || [];

  // Get the real achievement count for a section (base game + DLC achievements)
  const getSectionAchCount = (section) => {
    const baseCount = (section.achievements || []).length;
    const dlcCount = dlcs.flatMap(dlc => 
      (dlc.achievements || []).filter(a => a.sectionId === section.id)
    ).length;
    return baseCount + dlcCount;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-400" />
            Share to Community
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Share your organizers and challenges with other players of {game?.name}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={shareTab} onValueChange={setShareTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3 bg-black/50">
            <TabsTrigger value="organizers" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Layers className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Organizers</span>
            </TabsTrigger>
            <TabsTrigger value="challenges" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
              <Target className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Challenges</span>
            </TabsTrigger>
            <TabsTrigger value="planner" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
              <FolderPlus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Game Planner</span>
            </TabsTrigger>
          </TabsList>

          {/* Organizers Tab */}
          <TabsContent value="organizers" className="flex-1 overflow-hidden flex flex-col mt-4">
            {sections.length === 0 ? (
              <div className="text-center py-8">
                <Layers className="w-12 h-12 text-cyan-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No organizers to share</p>
                <p className="text-white/30 text-xs mt-1">Create organizers to group achievements first</p>
              </div>
            ) : (
              <div className="space-y-2 flex-1 overflow-y-auto">
                <p className="text-xs text-white/50 mb-2">Select organizers to share:</p>
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      setSelectedOrganizersToShare(prev =>
                        prev.includes(section.id)
                          ? prev.filter(id => id !== section.id)
                          : [...prev, section.id]
                      );
                    }}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedOrganizersToShare.includes(section.id)
                        ? 'bg-cyan-500/10 border-cyan-500/50'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`share-organizer-${section.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {selectedOrganizersToShare.includes(section.id) ? (
                        <CheckSquare className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{section.name}</p>
                        <p className="text-xs text-white/50">{getSectionAchCount(section)} achievements</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="flex-1 overflow-hidden flex flex-col mt-4">
            {!challenges || challenges.length === 0 ? (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-orange-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No challenges to share</p>
                <p className="text-white/30 text-xs mt-1">Create custom challenges first</p>
              </div>
            ) : (
              <div className="space-y-2 flex-1 overflow-y-auto">
                <p className="text-xs text-white/50 mb-2">Select challenges to share:</p>
                {challenges?.map((challenge) => (
                  <button
                    key={challenge.id}
                    onClick={() => {
                      setSelectedChallengesToShare(prev =>
                        prev.includes(challenge.id)
                          ? prev.filter(id => id !== challenge.id)
                          : [...prev, challenge.id]
                      );
                    }}
                    className={`w-full p-3 rounded-lg border text-left transition-colors ${
                      selectedChallengesToShare.includes(challenge.id)
                        ? 'bg-orange-500/10 border-orange-500/50'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`share-challenge-${challenge.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {selectedChallengesToShare.includes(challenge.id) ? (
                        <CheckSquare className="w-5 h-5 text-orange-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{challenge.name}</p>
                        <p className="text-xs text-white/50">
                          {challenge.prerequisiteAchievementIds?.length || 0} achievements
                          {challenge.difficulty && ` • ${challenge.difficulty}`}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Game Planner Tab */}
          <TabsContent value="planner" className="flex-1 overflow-hidden flex flex-col mt-4">
            <ProNotice />
            {sections.length < 2 ? (
              <div className="text-center py-8">
                <FolderPlus className="w-12 h-12 text-emerald-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">Need at least 2 organizers to create a Game Planner</p>
                <p className="text-white/30 text-xs mt-1">Game Planners bundle multiple organizers together</p>
              </div>
            ) : (
              <div className="space-y-4 flex-1 overflow-y-auto">
                <div className="space-y-2">
                  <Label className="text-white/70">Game Planner Name *</Label>
                  <Input
                    value={newGamePlanner.name}
                    onChange={(e) => setNewGamePlanner(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Complete Playthrough Guide"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/70">Description <span className="text-white/40 text-xs font-normal">(optional)</span></Label>
                  <Textarea
                    value={newGamePlanner.description}
                    onChange={(e) => setNewGamePlanner(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this planner is for and how to use it..."
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[60px] resize-none"
                    data-testid="planner-description-input"
                  />
                </div>

                <div>
                  <p className="text-xs text-white/50 mb-2">Select organizers to include ({selectedOrganizersForPlanner.length} selected):</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => {
                          setSelectedOrganizersForPlanner(prev =>
                            prev.includes(section.id)
                              ? prev.filter(id => id !== section.id)
                              : [...prev, section.id]
                          );
                        }}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          selectedOrganizersForPlanner.includes(section.id)
                            ? 'bg-emerald-500/10 border-emerald-500/50'
                            : 'bg-white/5 border-white/10 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {selectedOrganizersForPlanner.includes(section.id) ? (
                            <CheckSquare className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <Square className="w-5 h-5 text-white/40" />
                          )}
                          <div className="flex-1">
                            <p className="text-white font-medium">{section.name}</p>
                            <p className="text-xs text-white/50">
                              {getSectionAchCount(section)} achievements
                              {dlcs.some(dlc => (dlc.achievements || []).some(a => a.sectionId === section.id)) && (
                                <span className="ml-2 text-purple-400">• includes DLC</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Options */}
        <div className="space-y-3 pt-4 border-t border-white/10">
          {/* Guidelines */}
          <GuidelinesInlineSummary />

          {/* Tags input */}
          <div className="space-y-2">
            <Label className="text-xs text-white/50">Tags <span className="text-white/30">(optional - helps with search)</span></Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Add a tag and press Enter..."
                className="bg-black/50 border-white/10 text-sm flex-1"
                data-testid="share-tag-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddTag}
                className="border-white/20 text-white/70 px-3"
                data-testid="share-tag-add-btn"
              >
                Add
              </Button>
            </div>
            {shareTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {shareTags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs border border-purple-500/30"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-white transition-colors"
                      data-testid={`remove-tag-${tag}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Show submitter checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="showSubmitter"
              checked={shareShowSubmitter}
              onCheckedChange={setShareShowSubmitter}
            />
            <Label htmlFor="showSubmitter" className="text-sm text-white/70">
              Show my username on shared content
            </Label>
          </div>

          {/* Include grids (planner tab only) */}
          {shareTab === "planner" && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="includeScorecards"
                checked={includeScorecards}
                onCheckedChange={setIncludeScorecards}
                data-testid="include-scorecards-checkbox"
              />
              <Label htmlFor="includeScorecards" className="text-sm text-white/70">
                Bundle game grids with this planner
              </Label>
            </div>
          )}

          <p className="text-xs text-white/40">
            Submissions will be reviewed by moderators before being published
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="flex-1 border-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={shareSubmitting || (
              shareTab === "organizers" ? selectedOrganizersToShare.length === 0 :
              shareTab === "challenges" ? selectedChallengesToShare.length === 0 :
              shareTab === "planner" ? (selectedOrganizersForPlanner.length < 2 || !newGamePlanner.name.trim()) : true
            )}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            data-testid="share-submit-btn"
          >
            {shareSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                Submit for Review
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * BrowseCommunityDialog - Dialog for browsing and adopting community content
 */
export function BrowseCommunityDialog({
  open,
  onOpenChange,
  game,
  communityOrganizers,
  communityChallenges,
  communityGamePlanners,
  communityLoading,
  adoptingId,
  onAdoptOrganizer,
  onAdoptChallenge,
  onAdoptGamePlanner,
  communityChecklists,
  communityGrids,
  communityGridTemplates,
  onCopyChecklist,
  onAdoptGrid,
  onAdoptGridTemplate,
  communityMaps,
  onAdoptMap,
  onLikeMap,
}) {
  const [communityTab, setCommunityTab] = useState("organizers");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            Browse Community Content
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Find organizers, challenges, planners, checklists, and grids shared by other players for {game?.name}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={communityTab} onValueChange={setCommunityTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-7 bg-black/50">
            <TabsTrigger value="organizers" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400 text-xs px-1">
              <Layers className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Organizers</span>
            </TabsTrigger>
            <TabsTrigger value="challenges" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 text-xs px-1">
              <Target className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Challenges</span>
            </TabsTrigger>
            <TabsTrigger value="planners" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-xs px-1">
              <FolderPlus className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Planners</span>
            </TabsTrigger>
            <TabsTrigger value="checklists" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-xs px-1" data-testid="community-tab-checklists">
              <ListTodo className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Checklists</span>
            </TabsTrigger>
            <TabsTrigger value="grids" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-xs px-1" data-testid="community-tab-grids">
              <Grid3X3 className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Grids</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs px-1" data-testid="community-tab-templates">
              <Copy className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="maps" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400 text-xs px-1" data-testid="community-tab-maps">
              <MapPin className="w-3.5 h-3.5 mr-0.5" />
              <span className="hidden sm:inline">Maps</span>
            </TabsTrigger>
          </TabsList>

          {/* Organizers Tab */}
          <TabsContent value="organizers" className="flex-1 overflow-hidden flex flex-col mt-4">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              </div>
            ) : (communityOrganizers?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <Layers className="w-12 h-12 text-cyan-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community organizers available</p>
                <p className="text-white/30 text-xs mt-1">Be the first to share an organizer!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityOrganizers.map((organizer) => (
                  <div
                    key={organizer.id}
                    className="p-4 rounded-lg border border-cyan-500/20 bg-cyan-500/5 hover:bg-cyan-500/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <Layers className="w-4 h-4 text-cyan-400" />
                          {organizer.name}
                        </h4>
                        {organizer.description && (
                          <p className="text-white/50 text-sm mt-1">{organizer.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{organizer.achievementNames?.length || 0} achievements</span>
                          <span>{organizer.adoptedCount || 0} adopted</span>
                          {organizer.showSubmitter && organizer.submittedByUsername && (
                            <span>by {organizer.submittedByUsername}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAdoptOrganizer(organizer.id)}
                        disabled={adoptingId === organizer.id}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid={`adopt-organizer-${organizer.id}`}
                      >
                        {adoptingId === organizer.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Adopt
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="flex-1 overflow-hidden flex flex-col mt-4">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : (communityChallenges?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <Target className="w-12 h-12 text-orange-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community challenges available</p>
                <p className="text-white/30 text-xs mt-1">Be the first to share a challenge!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityChallenges.map((challenge) => (
                  <div
                    key={challenge.id}
                    className="p-4 rounded-lg border border-orange-500/20 bg-orange-500/5 hover:bg-orange-500/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <Target className="w-4 h-4 text-orange-400" />
                          {challenge.name}
                        </h4>
                        {challenge.description && (
                          <p className="text-white/50 text-sm mt-1">{challenge.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{challenge.prerequisiteAchievementNames?.length || 0} achievements</span>
                          {challenge.difficulty && <span className="capitalize">{challenge.difficulty}</span>}
                          <span>{challenge.adoptedCount || 0} adopted</span>
                          {challenge.showSubmitter && challenge.submittedByUsername && (
                            <span>by {challenge.submittedByUsername}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAdoptChallenge(challenge.id)}
                        disabled={adoptingId === challenge.id}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid={`adopt-challenge-${challenge.id}`}
                      >
                        {adoptingId === challenge.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Adopt
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Game Planners Tab */}
          <TabsContent value="planners" className="flex-1 overflow-hidden flex flex-col mt-4">
            <ProNotice />
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              </div>
            ) : (communityGamePlanners?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <FolderPlus className="w-12 h-12 text-emerald-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community Game Planners available</p>
                <p className="text-white/30 text-xs mt-1">Be the first to share a Game Planner!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityGamePlanners.map((planner) => (
                  <div
                    key={planner.id}
                    className="p-4 rounded-lg border border-emerald-500/20 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <FolderPlus className="w-4 h-4 text-emerald-400" />
                          {planner.name}
                        </h4>
                        {planner.description && (
                          <p className="text-white/50 text-sm mt-1">{planner.description}</p>
                        )}
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-white/60">
                            Includes {planner.organizerData?.length || 0} organizers
                            {(planner.scorecardData?.length || 0) > 0 && (
                              <span className="text-emerald-400"> + {planner.scorecardData.length} grid{planner.scorecardData.length > 1 ? 's' : ''}</span>
                            )}:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {(planner.organizerData || []).slice(0, 5).map((org, idx) => (
                              <span key={idx} className="text-xs px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded text-emerald-300">
                                {org.name}
                              </span>
                            ))}
                            {(planner.organizerData?.length || 0) > 5 && (
                              <span className="text-xs px-2 py-0.5 text-white/40">+{planner.organizerData.length - 5} more</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{planner.adoptedCount || 0} adopted</span>
                          {planner.showSubmitter && planner.submittedByUsername && (
                            <span>by {planner.submittedByUsername}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAdoptGamePlanner(planner.id)}
                        disabled={adoptingId === planner.id}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`adopt-planner-${planner.id}`}
                      >
                        {adoptingId === planner.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Adopt
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Checklists Tab */}
          <TabsContent value="checklists" className="flex-1 overflow-hidden flex flex-col mt-4" data-testid="community-checklists-content">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
              </div>
            ) : (communityChecklists?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <ListTodo className="w-12 h-12 text-blue-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community checklists for this game</p>
                <p className="text-white/30 text-xs mt-1">Share a checklist from the Share tab!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityChecklists.map((checklist) => (
                  <div
                    key={checklist.id}
                    className="p-4 rounded-lg border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 transition-colors"
                    data-testid={`community-checklist-${checklist.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <ListTodo className="w-4 h-4 text-blue-400" />
                          {checklist.title}
                        </h4>
                        {checklist.description && (
                          <p className="text-white/50 text-sm mt-1 line-clamp-2">{checklist.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{checklist.items?.length || 0} items</span>
                          {checklist.category && <span>{checklist.category}</span>}
                          <span>{checklist.copyCount || 0} copies</span>
                          {checklist.submittedByUsername && (
                            <span>by {checklist.submittedByUsername}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onCopyChecklist?.(checklist.id)}
                        disabled={adoptingId === checklist.id}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid={`copy-checklist-${checklist.id}`}
                      >
                        {adoptingId === checklist.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Grids Tab (from planners) */}
          <TabsContent value="grids" className="flex-1 overflow-hidden flex flex-col mt-4" data-testid="community-grids-content">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : (communityGrids?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <Grid3X3 className="w-12 h-12 text-purple-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community grids for this game</p>
                <p className="text-white/30 text-xs mt-1">Grids shared via planners will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityGrids.map((grid) => (
                  <div
                    key={grid.id}
                    className="p-4 rounded-lg border border-purple-500/20 bg-purple-500/5 hover:bg-purple-500/10 transition-colors"
                    data-testid={`community-grid-${grid.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <Grid3X3 className="w-4 h-4 text-purple-400" />
                          {grid.name}
                        </h4>
                        <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                          <span>{grid.rows?.length || 0} rows x {grid.cols?.length || 0} cols</span>
                          {grid.plannerName && <span>from planner: {grid.plannerName}</span>}
                          {grid.submittedByUsername && <span>by {grid.submittedByUsername}</span>}
                        </div>
                        {/* Mini preview */}
                        {grid.rows?.length > 0 && grid.cols?.length > 0 && (
                          <div className="mt-2 bg-zinc-900/60 rounded p-1.5 max-w-xs">
                            {grid.rows.slice(0, 3).map(r => (
                              <div key={r.id} className="flex gap-0.5 mt-0.5 first:mt-0">
                                {r.isDivider ? (
                                  <div className="flex-1 h-[1px] rounded my-0.5" style={{ backgroundColor: r.color || "#3f3f46" }} />
                                ) : (
                                  <>
                                    <span className="text-[9px] text-zinc-500 w-12 truncate">{r.name}</span>
                                    {grid.cols.slice(0, 5).map(c => (
                                      <div key={c.id} className="flex-1 h-3.5 rounded border border-zinc-800 bg-zinc-900/40" />
                                    ))}
                                  </>
                                )}
                              </div>
                            ))}
                            {grid.rows.length > 3 && <p className="text-[8px] text-zinc-600 text-center">+{grid.rows.length - 3} more</p>}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAdoptGrid?.(grid)}
                        disabled={adoptingId === grid.id}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid={`adopt-grid-${grid.id}`}
                      >
                        {adoptingId === grid.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Adopt
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Grid Templates Tab */}
          <TabsContent value="templates" className="flex-1 overflow-hidden flex flex-col mt-4" data-testid="community-templates-content">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : (communityGridTemplates?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <Copy className="w-12 h-12 text-amber-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No grid templates for this game</p>
                <p className="text-white/30 text-xs mt-1">Save a grid as a template and share it!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityGridTemplates.map((template) => (
                  <div
                    key={template.id}
                    className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                    data-testid={`community-template-${template.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-white font-medium flex items-center gap-2">
                          <Copy className="w-4 h-4 text-amber-400" />
                          {template.name}
                        </h4>
                        {template.description && (
                          <p className="text-white/50 text-sm mt-1 line-clamp-2">{template.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                          <span>{template.rows?.length || 0} rows x {template.cols?.length || 0} cols</span>
                          <span>Used {template.useCount || 0}x</span>
                          {template.submittedByUsername && (
                            <span>by {template.submittedByUsername}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => onAdoptGridTemplate?.(template.id)}
                        disabled={adoptingId === template.id}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`use-template-${template.id}`}
                      >
                        {adoptingId === template.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Use
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Maps Tab */}
          <TabsContent value="maps" className="flex-1 overflow-hidden flex flex-col mt-4">
            {communityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : (communityMaps?.length || 0) === 0 ? (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-amber-400/30 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No community maps available</p>
                <p className="text-white/30 text-xs mt-1">Be the first to share a map!</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-2">
                {communityMaps.map((map) => (
                  <div
                    key={map.id}
                    className="p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                    data-testid={`community-map-${map.id}`}
                  >
                    <div className="flex gap-3">
                      <div className="w-20 h-14 rounded-md overflow-hidden border border-white/10 flex-shrink-0">
                        <img src={map.mapImageUrl} alt={map.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                          {map.title}
                        </h4>
                        {map.description && (
                          <p className="text-white/50 text-xs mt-0.5 line-clamp-1">{map.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-white/40">
                          <span>{(map.pins || []).length} pins</span>
                          {map.sectionName && <span>Section: {map.sectionName}</span>}
                          <span>Adopted {map.adoptedCount || 0}x</span>
                          {map.showSubmitter && map.submittedByUsername && (
                            <span>by {map.submittedByUsername}</span>
                          )}
                        </div>
                        {(map.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {map.tags.map(t => (
                              <span key={t} className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          onClick={() => onAdoptMap?.(map.id)}
                          disabled={adoptingId === map.id}
                          className="bg-amber-600 hover:bg-amber-700 text-xs h-7"
                          data-testid={`adopt-map-${map.id}`}
                        >
                          {adoptingId === map.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Download className="w-3 h-3 mr-1" />
                              Adopt
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onLikeMap?.(map.id)}
                          className={`text-xs h-7 ${map.isLikedByUser ? "text-red-400" : "text-white/40 hover:text-red-400"}`}
                          data-testid={`like-map-${map.id}`}
                        >
                          <Heart className={`w-3 h-3 mr-1 ${map.isLikedByUser ? "fill-red-400" : ""}`} />
                          {map.likesCount || 0}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
